import DOMPurify from 'dompurify';
import { safeHref } from './safeHref';

const ALLOWED_TAGS = [
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'em',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'ol',
  'p',
  'pre',
  's',
  'span',
  'strong',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
];

const ALLOWED_ATTR = ['alt', 'class', 'href', 'rel', 'src', 'target', 'title'];

function applySafeUri(_node, data) {
  if (data.attrName !== 'href' && data.attrName !== 'src') return;
  const next = safeHref(data.attrValue);
  if (!next) {
    data.keepAttr = false;
    return;
  }
  data.attrValue = next;
}

const RICH_STYLE_PROPS = ['color', 'background-color'];
const RICH_STYLE_VALUE = /^(?:#[0-9a-f]{3,8}|rgba?\([\d\s.,%]+\)|[a-z]+)$/i;

// Quill colours are inline styles; keep only those, drop any other CSS.
function applyRichStyle(_node, data) {
  if (data.attrName !== 'style') return;
  const kept = String(data.attrValue)
    .split(';')
    .map((decl) => {
      const idx = decl.indexOf(':');
      if (idx < 0) return null;
      const prop = decl.slice(0, idx).trim().toLowerCase();
      const value = decl.slice(idx + 1).trim();
      if (!RICH_STYLE_PROPS.includes(prop) || !RICH_STYLE_VALUE.test(value)) {
        return null;
      }
      return `${prop}: ${value}`;
    })
    .filter(Boolean);
  if (!kept.length) {
    data.keepAttr = false;
    return;
  }
  data.attrValue = kept.join('; ');
}

/**
 * Blank-target anchors must not keep window.opener. Merge onto any
 * existing rel tokens and drop opener so a stored rel cannot undo it.
 */
function applyBlankTargetRel(node) {
  if (!node || node.nodeName !== 'A') return;
  if (node.getAttribute('target') !== '_blank') return;
  const tokens = String(node.getAttribute('rel') || '')
    .split(/\s+/)
    .filter((token) => token && token !== 'opener');
  if (!tokens.includes('noopener')) tokens.push('noopener');
  if (!tokens.includes('noreferrer')) tokens.push('noreferrer');
  node.setAttribute('rel', tokens.join(' '));
}

// Own instance so these hooks never leak into other DOMPurify callers.
const purify = DOMPurify(window);
purify.addHook('uponSanitizeAttribute', applySafeUri);
purify.addHook('uponSanitizeAttribute', applyRichStyle);
purify.addHook('afterSanitizeAttributes', applyBlankTargetRel);

/**
 * Sanitize marked HTML before v-html. Strips script/iframe/object,
 * event handlers, and javascript:/data:/protocol-relative hrefs.
 * URI policy is safeHref — same helper page nodes use. Blank-target
 * anchors always get rel="noopener noreferrer".
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeRenderedHtml(html) {
  if (!html) return '';
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_ATTR: ['srcdoc'],
  });
}

/**
 * Quill rich text: sanitizeRenderedHtml plus text/background colour styles.
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeRichHtml(html) {
  if (!html) return '';
  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: [...ALLOWED_ATTR, 'style'],
    FORBID_ATTR: ['srcdoc'],
  });
}

const EMBED_IFRAME_HOST = 'cdn.embedly.com';
const EMBED_IFRAME_ATTRS = [
  'allow',
  'allowfullscreen',
  'class',
  'frameborder',
  'height',
  'scrolling',
  'src',
  'title',
  'width',
];

function embedSrc(value) {
  const raw = String(value || '').trim();
  try {
    const url = new URL(raw.startsWith('//') ? `https:${raw}` : raw);
    if (url.protocol !== 'https:' || url.hostname !== EMBED_IFRAME_HOST) {
      return '';
    }
    return url.toString();
  } catch (err) {
    return '';
  }
}

function firstEmbedIframe(html) {
  const doc = new DOMParser().parseFromString(String(html), 'text/html');
  for (const iframe of doc.body.querySelectorAll('iframe')) {
    const src = embedSrc(iframe.getAttribute('src'));
    if (src) {
      iframe.setAttribute('src', src);
      return iframe.outerHTML;
    }
  }
  return '';
}

/**
 * Embedly oEmbed HTML: first allowed iframe, Embedly CDN src, no event handlers.
 * Uses the same DOMPurify instance as sanitizeRenderedHtml.
 *
 * @param {string} html
 * @returns {string}
 */
export function sanitizeEmbedHtml(html) {
  const iframe = firstEmbedIframe(html || '');
  if (!iframe) return '';
  return purify.sanitize(iframe, {
    ALLOWED_TAGS: ['iframe'],
    ALLOWED_ATTR: EMBED_IFRAME_ATTRS,
    FORBID_ATTR: ['srcdoc'],
  });
}

// Drop whitespace and C0 control chars (code <= 0x20) so a scheme hidden as
// `java\tscript:` can't slip past SVG_DANGEROUS_URI. Done by char code rather
// than a control-char regex literal, which trips eslint's no-control-regex.
function stripUriControls(value) {
  let out = '';
  for (const ch of String(value)) {
    if (ch.charCodeAt(0) > 0x20) {
      out += ch;
    }
  }
  return out;
}

const SVG_DANGEROUS_TAGS = ['script', 'iframe'];
const SVG_URI_ATTRS = ['href', 'xlink:href', 'src'];
const SVG_DANGEROUS_URI = /^(?:javascript|vbscript|data):/i;

/**
 * Sanitize a mermaid-rendered SVG string before it is assigned to innerHTML.
 *
 * @param {string} svg
 * @returns {string}
 */
export function sanitizeRenderedSvg(svg) {
  if (!svg) return '';
  const doc = new DOMParser().parseFromString(String(svg), 'text/html');
  const svgEl = doc.body.querySelector('svg');
  if (!svgEl) return '';

  for (const el of [svgEl, ...svgEl.querySelectorAll('*')]) {
    const tag = (el.tagName || '').toLowerCase();
    if (SVG_DANGEROUS_TAGS.includes(tag)) {
      el.remove();
      continue;
    }
    for (const attr of Array.from(el.attributes || [])) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
        continue;
      }
      const uri = stripUriControls(attr.value);
      if (SVG_URI_ATTRS.includes(name) && SVG_DANGEROUS_URI.test(uri)) {
        el.removeAttribute(attr.name);
      }
    }
  }

  return svgEl.outerHTML;
}
